import type { Cel, Groep, Kubus } from '../../gedeeld/types'
import { api } from './api'

/* ===========================================================================
 *  Rekenen met de kubus
 *
 *  De functie levert per boekjaar elke combinatie van bv, rekening en
 *  periode met het bedrag (grootboek.ts daar). Het grootboek, de
 *  vergelijking en de aansluiting rekenen hier uit dezelfde cellen; zo tellen
 *  twee schermen nooit op twee manieren op.
 *
 *  Mutaties of stand
 *  -----------------
 *
 *  Een W&V-rekening (omzet, kosten) telt wat er in de gekozen periodes
 *  gebeurde. Een balansrekening (bank, debiteuren) heeft daarnaast een STAND:
 *  alles tot en met de laatste periode, inclusief de beginbalans (periode 0).
 *  "Stand" kan alleen uit de saldi -- de boekingsregels hebben geen
 *  beginbalans -- en geldt alleen voor balansrekeningen.
 * =========================================================================== */

const cache = new Map<string, Promise<Kubus>>()

export function laadKubus(bvs: string[], jaar: number, bron: 'saldi' | 'boekingen', kostenplaats = false): Promise<Kubus> {
  const sleutel = [jaar, bron, kostenplaats ? 1 : 0, [...bvs].sort().join(',')].join('|')
  let p = cache.get(sleutel)
  if (!p) {
    p = api.kubus(bvs, jaar, bron, kostenplaats)
    cache.set(sleutel, p)
    p.catch(() => cache.delete(sleutel))
    /* Niet eeuwig bewaren: het archief wordt bijgewerkt. */
    setTimeout(() => cache.delete(sleutel), 10 * 60_000)
  }
  return p
}

export function vergeetKubussen() { cache.clear() }

export type Rijen = 'rekening' | 'groep' | 'kostenplaats'
export type Modus = 'mutatie' | 'stand'

export interface Snede {
  bvs: string[]
  van: number
  tot: number
  modus: Modus
}

/** Is deze rekening een balansrekening? Uit het schema van Exact, anders uit de indeling. */
export function isBalans(k: Kubus, bv: string, rekening: string): boolean {
  const s = k.schema[`${bv}|${rekening}`]
  if (s?.balans) return s.balans === 'B'
  const g = k.indeling.plek[`${bv}|${rekening}`]
  const groep = g ? k.indeling.groepen.find((x) => x.id === g) : undefined
  if (groep) return groep.soort === 'bezit' || groep.soort === 'schuld'
  /* Het Nederlandse schema: 0 tot en met 3 is balans. Alleen als niets anders iets zegt. */
  return /^[0-3]/.test(rekening)
}

export function rekeningNaam(k: Kubus, bv: string, rekening: string): string {
  return k.schema[`${bv}|${rekening}`]?.naam || k.namen[`${bv}|${rekening}`] || ''
}

export function telt(k: Kubus, c: Cel, s: Snede): boolean {
  if (!s.bvs.includes(c[0])) return false
  const p = c[2]
  if (s.modus === 'stand' && isBalans(k, c[0], c[1])) return p <= s.tot
  return p >= s.van && p <= s.tot
}

export interface Bedragen { saldo: number; debet: number; credit: number; aantal: number }

const leeg = (): Bedragen => ({ saldo: 0, debet: 0, credit: 0, aantal: 0 })

function erbij(b: Bedragen, c: Cel) {
  b.saldo += c[3]
  b.debet += c[4]
  b.credit += c[5]
  b.aantal += c[6]
}

/* ------------------------------------------------------------------ *
 *  De sleutel van een rij
 * ------------------------------------------------------------------ */

export const NIET_INGEDEELD = '__niet'

export function rijSleutel(k: Kubus, c: Cel, rijen: Rijen): string {
  if (rijen === 'rekening') return c[1]
  if (rijen === 'kostenplaats') return c[7] ?? ''
  return k.indeling.plek[`${c[0]}|${c[1]}`] ?? NIET_INGEDEELD
}

export interface RijInfo {
  sleutel: string
  naam: string
  /** Voor de indeling: de groep, om op te sorteren en in te springen. */
  groep?: Groep
  diepte: number
  balans: boolean
  /** Bij rekening: in welke bv's hij voorkomt (voor het doorklikken). */
  bvs: Set<string>
}

/** Groepen met hun voorouders, in de volgorde van de indeling. */
export function groepPad(groepen: Groep[], id: string): Groep[] {
  const pad: Groep[] = []
  let g = groepen.find((x) => x.id === id)
  const gezien = new Set<string>()
  while (g && !gezien.has(g.id)) {
    gezien.add(g.id)
    pad.unshift(g)
    g = g.ouder ? groepen.find((x) => x.id === g!.ouder) : undefined
  }
  return pad
}

/**
 * Optellen: per rij-sleutel, en per kolom (een functie die een cel aan een
 * kolom toewijst, of null als hij nergens hoort).
 */
export function tel(
  k: Kubus,
  rijen: Rijen,
  kolomVan: (c: Cel) => string | null,
  filter: (c: Cel) => boolean,
): { rijen: Map<string, RijInfo>; cellen: Map<string, Map<string, Bedragen>> } {
  const info = new Map<string, RijInfo>()
  const cellen = new Map<string, Map<string, Bedragen>>()
  for (const c of k.cellen) {
    if (!filter(c)) continue
    const kol = kolomVan(c)
    if (kol === null) continue
    const s = rijSleutel(k, c, rijen)
    let i = info.get(s)
    if (!i) {
      const groep = rijen === 'groep' ? k.indeling.groepen.find((g) => g.id === s) : undefined
      i = {
        sleutel: s,
        naam: rijen === 'rekening' ? rekeningNaam(k, c[0], c[1])
          : rijen === 'kostenplaats' ? (k.kostenplaatsen[s] ?? (s ? '' : '(geen kostenplaats)'))
          : groep?.naam ?? 'Niet ingedeeld',
        groep,
        diepte: groep ? groepPad(k.indeling.groepen, groep.id).length - 1 : 0,
        balans: rijen === 'groep' && groep ? groep.soort === 'bezit' || groep.soort === 'schuld' : isBalans(k, c[0], c[1]),
        bvs: new Set(),
      }
      info.set(s, i)
    }
    i.bvs.add(c[0])
    if (!i.naam && rijen === 'rekening') i.naam = rekeningNaam(k, c[0], c[1])
    let rij = cellen.get(s)
    if (!rij) { rij = new Map(); cellen.set(s, rij) }
    let b = rij.get(kol)
    if (!b) { b = leeg(); rij.set(kol, b) }
    erbij(b, c)
  }
  return { rijen: info, cellen }
}

/* ------------------------------------------------------------------ *
 *  De boom van de indeling
 *
 *  Een groep kan een ouder hebben (0128). Dan hoort de ouder te laten zien
 *  wat er onder hem valt: zijn eigen rekeningen plus die van zijn kinderen.
 *  Een ouder zonder eigen rekeningen moet er ook staan, anders hangt
 *  "Personeel" los onder niets. De totalen onderaan tellen elke rekening één
 *  keer: die rekenen met de eigen bedragen, niet met deze optelling.
 * ------------------------------------------------------------------ */

export interface Boom {
  rijen: RijInfo[]
  /** Heeft deze rij kinderen? Dan is zijn bedrag een subtotaal. */
  ouders: Set<string>
  /** Per rij de sleutels van alles eronder (zichzelf meegeteld). */
  onder: Map<string, string[]>
}

export function boom(lijst: RijInfo[], groepen: Groep[]): Boom {
  const alle = new Map(lijst.map((r) => [r.sleutel, r]))
  const ouders = new Set<string>()
  for (const r of lijst) {
    if (!r.groep) continue
    for (const g of groepPad(groepen, r.groep.id).slice(0, -1)) {
      ouders.add(g.id)
      if (!alle.has(g.id)) {
        alle.set(g.id, {
          sleutel: g.id, naam: g.naam, groep: g, diepte: groepPad(groepen, g.id).length - 1,
          balans: g.soort === 'bezit' || g.soort === 'schuld', bvs: new Set(),
        })
      }
    }
  }
  const onder = new Map<string, string[]>()
  for (const r of alle.values()) onder.set(r.sleutel, [r.sleutel])
  for (const r of lijst) {
    if (!r.groep) continue
    for (const g of groepPad(groepen, r.groep.id).slice(0, -1)) onder.get(g.id)!.push(r.sleutel)
  }
  return { rijen: sorteerRijen([...alle.values()], 'groep', groepen), ouders, onder }
}

/** De volgorde van de rijen: balans eerst, dan W&V; binnen een blok op code of indeling. */
export function sorteerRijen(lijst: RijInfo[], rijen: Rijen, groepen: Groep[]): RijInfo[] {
  const volg = (i: RijInfo): string => {
    if (rijen !== 'groep' || !i.groep) return i.sleutel
    return groepPad(groepen, i.groep.id).map((g) => String(g.volgorde).padStart(6, '0') + g.code).join('/')
  }
  return [...lijst].sort((a, b) =>
    Number(b.balans) - Number(a.balans)
    || (a.sleutel === NIET_INGEDEELD ? 1 : 0) - (b.sleutel === NIET_INGEDEELD ? 1 : 0)
    || volg(a).localeCompare(volg(b), 'nl', { numeric: true }))
}

/*
 * Een periode is een periode van het boekjaar, geen maand. Bij bijna elke bv
 * valt periode 1 samen met januari, maar niet bij allemaal: het eerste
 * boekjaar 2026 van Groenlo, Steenwijk en Wehl begon eind november 2025
 * (gezien in de perioden op 7 oktober 2026). Daarom staat er "P1" en de maand
 * alleen als hint.
 */
const MAAND = ['', 'jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']

export function periodeTekst(p: number): string {
  return p === 0 ? 'Begin' : `P${p}`
}

export function periodeHint(p: number): string {
  return p === 0 ? 'Beginbalans (periode 0)' : `Periode ${p} van het boekjaar${MAAND[p] ? ` (meestal ${MAAND[p]})` : ''}`
}
